import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, X, Check, Flag, Calendar } from 'lucide-react';
import { get, post, patch, del } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Textarea } from '@/components/atoms/Textarea';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Badge } from '@/components/atoms/Badge';
import { toast } from '@/store/toastStore';
import { confirmModal } from '@/store/confirmStore';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/lib/cn';

/**
 * 어드민 — 공유 할 일 목록.
 *  - 모든 어드민이 함께 조회/생성
 *  - 본인 작성 글만 수정/삭제 (super 는 모두)
 *  - 정렬: 미완료 우선 → high priority 우선 → 최신순
 */

type StatusFilter = 'all' | 'pending' | 'completed';

interface TodoRow {
  id: number;
  title: string;
  description: string | null;
  status: 'pending' | 'completed';
  priority: 'high' | 'normal';
  due_date: string | null;
  created_by: string | null;
  assignee_id: string | null;
  creator_nickname: string | null;
  assignee_nickname: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

interface ListResponse {
  rows: TodoRow[];
  total: number;
  page: number;
  limit: number;
}

export function AdminTodosPage() {
  const me = useAuthStore((s) => s.me);
  const isSuper = me?.adminRole === 'super';
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [editing, setEditing] = useState<TodoRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'todos', statusFilter],
    queryFn: () => get<ListResponse>(`/admin/todos?status=${statusFilter}&limit=100`),
    staleTime: 15_000,
  });

  const rows = data?.rows ?? [];
  const pendingCount = rows.filter((r) => r.status === 'pending').length;

  const createMut = useMutation({
    mutationFn: (body: { title: string; description?: string; priority?: 'high' | 'normal'; dueDate?: string }) =>
      post<{ id: number }>('/admin/todos', body),
    onSuccess: () => {
      toast.success('추가됐어요.');
      qc.invalidateQueries({ queryKey: ['admin', 'todos'] });
    },
    onError: () => toast.error('추가에 실패했어요.'),
  });

  const updateMut = useMutation({
    mutationFn: (vars: { id: number; body: Record<string, any> }) =>
      patch(`/admin/todos/${vars.id}`, vars.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'todos'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message ?? '저장에 실패했어요.');
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => del(`/admin/todos/${id}`),
    onSuccess: () => {
      toast.success('삭제됐어요.');
      qc.invalidateQueries({ queryKey: ['admin', 'todos'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message ?? '삭제에 실패했어요.');
    },
  });

  const canEditRow = (row: TodoRow) => isSuper || row.created_by === me?.userId;

  return (
    <div className="w-full">
      <header className="mb-6">
        <h1 className="text-2xl font-extrabold">할 일</h1>
        <p className="mt-1 text-xs text-[var(--color-text-sub)]">
          어드민 간 공유되는 운영 task 목록이에요. 본인이 작성한 항목만 수정/삭제할 수 있어요 (super 는 모두).
        </p>
      </header>

      {/* 추가 폼 — 항상 상단에 노출 */}
      <CreateForm onSubmit={(body) => createMut.mutate(body)} loading={createMut.isPending} />

      {/* 필터 탭 */}
      <div className="mt-6 mb-4 flex w-full gap-1 rounded-md bg-[var(--color-tab-bg)] p-1">
        {([
          { key: 'pending',   label: '진행 중', count: statusFilter === 'all' ? null : pendingCount },
          { key: 'completed', label: '완료', count: null },
          { key: 'all',       label: '전체', count: null },
        ] as const).map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setStatusFilter(t.key)}
            className={cn(
              'flex-1 rounded-md px-3 py-1.5 text-sm transition-colors',
              statusFilter === t.key
                ? 'bg-[var(--color-tab-active)] font-semibold text-[var(--color-text)]'
                : 'text-[var(--color-text-sub)] hover:bg-[var(--color-tab-hover)]',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* 리스트 */}
      {isLoading ? (
        <Skeleton className="h-64" />
      ) : rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-[var(--color-border)] p-8 text-center text-sm text-[var(--color-text-sub)]">
          {statusFilter === 'pending' ? '진행 중인 항목이 없어요. 위에서 새 항목을 추가해보세요.' :
           statusFilter === 'completed' ? '완료된 항목이 없어요.' :
           '등록된 항목이 없어요.'}
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <TodoItem
              key={r.id}
              row={r}
              editable={canEditRow(r)}
              isEditing={editing?.id === r.id}
              onStartEdit={() => setEditing(r)}
              onCancelEdit={() => setEditing(null)}
              onToggle={() =>
                updateMut.mutate({
                  id: r.id,
                  body: { status: r.status === 'pending' ? 'completed' : 'pending' },
                })
              }
              onSave={(body) => {
                updateMut.mutate(
                  { id: r.id, body },
                  { onSuccess: () => { toast.success('저장됐어요.'); setEditing(null); } },
                );
              }}
              onDelete={async () => {
                const ok = await confirmModal({
                  title: '할 일 삭제',
                  message: `"${r.title}" 항목을 삭제할까요? 복원할 수 없어요.`,
                  confirmLabel: '삭제',
                  cancelLabel: '취소',
                  tone: 'danger',
                });
                if (ok) deleteMut.mutate(r.id);
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/* ─── 추가 폼 ──────────────────────────────────────────────────────── */
function CreateForm({
  onSubmit,
  loading,
}: {
  onSubmit: (body: { title: string; description?: string; priority?: 'high' | 'normal'; dueDate?: string }) => void;
  loading: boolean;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<'high' | 'normal'>('normal');
  const [dueDate, setDueDate] = useState('');
  const [expanded, setExpanded] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    onSubmit({
      title: title.trim(),
      description: description.trim() || undefined,
      priority,
      dueDate: dueDate || undefined,
    });
    setTitle('');
    setDescription('');
    setPriority('normal');
    setDueDate('');
    setExpanded(false);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
    >
      <div className="flex gap-2">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onFocus={() => setExpanded(true)}
          placeholder="할 일을 입력하고 Enter (예: 카카오 동의항목 활성화 검수 신청)"
          maxLength={200}
          className="flex-1"
        />
        <Button type="submit" size="md" loading={loading} disabled={!title.trim()}>
          <Plus className="h-4 w-4" />
          추가
        </Button>
      </div>
      {expanded && (
        <div className="mt-3 space-y-3">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="설명 (선택)"
            rows={2}
            maxLength={2000}
          />
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              <label className="text-xs text-[var(--color-text-sub)]">우선순위</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as 'high' | 'normal')}
                className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-sm"
              >
                <option value="normal">보통</option>
                <option value="high">높음</option>
              </select>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <label className="text-xs text-[var(--color-text-sub)]">마감일</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-sm"
              />
            </div>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="ml-auto text-xs text-[var(--color-text-sub)] hover:underline"
            >
              간단히
            </button>
          </div>
        </div>
      )}
    </form>
  );
}

/* ─── 개별 row ─────────────────────────────────────────────────────── */
function TodoItem({
  row,
  editable,
  isEditing,
  onStartEdit,
  onCancelEdit,
  onToggle,
  onSave,
  onDelete,
}: {
  row: TodoRow;
  editable: boolean;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onToggle: () => void;
  onSave: (body: { title: string; description: string | null; priority: 'high' | 'normal'; dueDate: string | null }) => void;
  onDelete: () => void;
}) {
  const [editTitle, setEditTitle] = useState(row.title);
  const [editDesc, setEditDesc] = useState(row.description ?? '');
  const [editPriority, setEditPriority] = useState(row.priority);
  const [editDue, setEditDue] = useState(row.due_date ?? '');

  const isOverdue =
    row.status === 'pending' &&
    !!row.due_date &&
    new Date(row.due_date).getTime() < Date.now() - 24 * 60 * 60 * 1000;

  return (
    <li
      className={cn(
        'rounded-2xl border bg-[var(--color-surface)] p-4 transition-colors',
        row.status === 'completed'
          ? 'border-[var(--color-border)] opacity-60'
          : isOverdue
          ? 'border-[var(--color-fall)]/50 bg-[var(--color-fall)]/5'
          : 'border-[var(--color-border)]',
      )}
    >
      {isEditing ? (
        /* ── 편집 모드 ──────────────────────────────── */
        <div className="space-y-3">
          <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} maxLength={200} />
          <Textarea
            value={editDesc}
            onChange={(e) => setEditDesc(e.target.value)}
            rows={2}
            placeholder="설명 (선택)"
            maxLength={2000}
          />
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={editPriority}
              onChange={(e) => setEditPriority(e.target.value as 'high' | 'normal')}
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-sm"
            >
              <option value="normal">보통</option>
              <option value="high">높음</option>
            </select>
            <input
              type="date"
              value={editDue}
              onChange={(e) => setEditDue(e.target.value)}
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-sm"
            />
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="ghost" onClick={onCancelEdit}>
                <X className="h-3.5 w-3.5" />
                취소
              </Button>
              <Button
                size="sm"
                onClick={() =>
                  onSave({
                    title: editTitle.trim() || row.title,
                    description: editDesc.trim() || null,
                    priority: editPriority,
                    dueDate: editDue || null,
                  })
                }
              >
                <Check className="h-3.5 w-3.5" />
                저장
              </Button>
            </div>
          </div>
        </div>
      ) : (
        /* ── 보기 모드 ──────────────────────────────── */
        <div className="flex items-start gap-3">
          {/* 체크박스 — 누구나 토글 가능 (운영 task 공유 특성상) */}
          <button
            type="button"
            onClick={onToggle}
            aria-label={row.status === 'pending' ? '완료 처리' : '미완료로 되돌리기'}
            className={cn(
              'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors',
              row.status === 'completed'
                ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                : 'border-[var(--color-border)] hover:border-[var(--color-primary)]',
            )}
          >
            {row.status === 'completed' && <Check className="h-3 w-3" />}
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span
                className={cn(
                  'text-sm font-semibold',
                  row.status === 'completed' && 'text-[var(--color-text-sub)] line-through',
                )}
              >
                {row.title}
              </span>
              {row.priority === 'high' && (
                <Badge variant="fall">
                  <Flag className="h-3 w-3" />
                  높음
                </Badge>
              )}
              {row.due_date && (
                <Badge variant={isOverdue ? 'fall' : 'default'}>
                  <Calendar className="h-3 w-3" />
                  {row.due_date}
                </Badge>
              )}
            </div>
            {row.description && (
              <p className="mt-1 whitespace-pre-line text-xs text-[var(--color-text-sub)]">{row.description}</p>
            )}
            <p className="mt-1 text-[11px] text-[var(--color-text-sub)]">
              {row.creator_nickname ?? '-'} 작성 · {new Date(row.created_at).toLocaleString('ko-KR', {
                year: '2-digit', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
              })}
              {row.completed_at && (
                <> · 완료 {new Date(row.completed_at).toLocaleString('ko-KR', {
                  month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
                })}</>
              )}
            </p>
          </div>
          {editable && (
            <div className="flex shrink-0 items-center gap-1">
              <Button size="sm" variant="ghost" onClick={onStartEdit} aria-label="수정">
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={onDelete} aria-label="삭제">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
